<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { AlertV2, ButtonV2, Modal, SliderV2, toast } from '@Shelly/shelly-design-system/index';
	import { percent_from_value_range } from 'src/helpers/utilities';
	import {
		DEFAULT_MIN_CLASSIFICATION_SCORE,
		MAX_SELECTABLE_CLASSIFICATION_SCORE,
		MIN_CLASSIFICATION_SCORE,
		getCameraCloudSettings,
		setCameraCloudSettings,
	} from 'src/services/cam-cloud/recordings';

	export let deviceId: string;
	export let showModal = false;

	/**
	 * The stored threshold in percent. Bindable, so an opener that already knows
	 * it can show it elsewhere; null means unknown and the modal reads it when it
	 * opens.
	 */
	export let percent: number | null = null;

	const SCORE_RANGE = [0, 1];
	const PERCENT_PER_SCORE = 100;
	const STEP_PERCENT = 5;
	const BALANCED_PERCENT = 50;
	const MIN_PERCENT = percent_from_value_range(MIN_CLASSIFICATION_SCORE, SCORE_RANGE);
	const MAX_PERCENT = percent_from_value_range(MAX_SELECTABLE_CLASSIFICATION_SCORE, SCORE_RANGE);
	const DEFAULT_PERCENT = percent_from_value_range(DEFAULT_MIN_CLASSIFICATION_SCORE, SCORE_RANGE);

	const dispatch = createEventDispatcher();

	/** What the slider edits; only written back to `percent` once saved. */
	let draft = DEFAULT_PERCENT;
	let loading = false;
	let saving = false;

	$: title = $format('camera_cloud_confidence_title', { default: 'AI Detection confidence' });
	$: hasChanges = draft !== percent;
	$: markers = [
		{
			value: MIN_PERCENT,
			displayValue: $format('camera_cloud_confidence_marker_all', { default: 'All' }),
			showLabel: true,
		},
		{
			value: BALANCED_PERCENT,
			displayValue: $format('camera_cloud_confidence_marker_balanced', { default: 'Balanced' }),
			showLabel: true,
		},
		{
			value: MAX_PERCENT,
			displayValue: $format('camera_cloud_confidence_marker_strict', { default: 'Strict' }),
			showLabel: true,
		},
	];
	$: if (showModal) handleOpen();

	/*
	 * Opening is the only thing that reads the setting, so an opener that merely
	 * mounts this modal never hits the endpoint.
	 */
	const handleOpen = async () => {
		if (percent === null) {
			loading = true;
			try {
				const settings = await getCameraCloudSettings(deviceId);
				percent = percent_from_value_range(
					settings?.minClassificationScore ?? DEFAULT_MIN_CLASSIFICATION_SCORE,
					SCORE_RANGE,
				);
			} catch (error) {
				console.log('Failed to load detection confidence', error);
				percent = DEFAULT_PERCENT;
			}
			loading = false;
		}

		draft = percent;
	};

	const handleClose = () => {
		if (saving) return;

		dispatch('close');
	};

	const handleValueChange = (event: CustomEvent<{ value: number }>) => {
		draft = event.detail.value;
	};

	const handleSave = async () => {
		if (!hasChanges) {
			dispatch('close');

			return;
		}

		saving = true;
		try {
			await setCameraCloudSettings(deviceId, {
				minClassificationScore: draft / PERCENT_PER_SCORE,
			});
			percent = draft;
			dispatch('close');
			toast.open({
				type: 'success',
				title: $format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
			});
		} catch (error) {
			console.log('Failed to save detection confidence', error);
			toast.open({
				type: 'danger',
				title: $format('camera_cloud_confidence_error', {
					default: 'Could not save the detection confidence. Please try again.',
				}),
			});
		}
		saving = false;
	};
</script>

<Modal {showModal} {title} backdropClose={false} on:close={handleClose}>
	<svelte:fragment slot="content">
		<AlertV2
			class="mb-m"
			heading={$format('camera_cloud_confidence_alert_title', {
				default: 'Applies to new recordings only',
			})}
			message={$format('camera_cloud_confidence_alert_message', {
				default: 'Recordings you already have keep the objects marked when they were captured.',
			})}
		/>
		<SliderV2
			id="camera-cloud-confidence"
			value={draft}
			on:value_change={handleValueChange}
			disabled={loading || saving}
			isLoading={loading}
			min={MIN_PERCENT}
			max={MAX_PERCENT}
			step={STEP_PERCENT}
			type="incremented"
			unit="%"
			a11yProps={{
				ariaLabel: title,
				ariaLabelValue: $format('camera_cloud_confidence_value', {
					default: 'Detection confidence value',
				}),
			}}
			lineMarkers={markers}
		/>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="mr-s"
			variant="secondary"
			disabled={saving}
			on:click={handleClose}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="primary"
			loading={saving}
			disabled={!hasChanges || loading || saving}
			on:click={handleSave}
			aria-label={$format('button_save', { default: 'Save' })}
		>
			{$format('button_save', { default: 'Save' })}
		</ButtonV2>
	</div>
</Modal>
